import { BLUE, CHROME, DANGER, INK } from '../Theme/palette';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface IOtpInputProps {
  /** Called when all 6 digits are filled (including via paste). Auto-submits. */
  onComplete: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Increment this value to clear all inputs (e.g. after an error). */
  resetKey?: number;
  /** Renders boxes with red border when true. */
  error?: boolean;
}

const LENGTH = 6;

export const OtpInput: React.FC<IOtpInputProps> = ({
  onComplete,
  disabled = false,
  autoFocus = true,
  resetKey = 0,
  error = false,
}) => {
  const [digits, setDigits] = useState<string[]>(Array(LENGTH).fill(''));
  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(LENGTH).fill(null));

  const focusAt = useCallback((index: number) => {
    const target = Math.max(0, Math.min(LENGTH - 1, index));
    inputRefs.current[target]?.focus();
  }, []);

  // Clear and refocus when resetKey changes
  useEffect(() => {
    setDigits(Array(LENGTH).fill(''));
    if (autoFocus) {
      // defer so the render settles first
      setTimeout(() => focusAt(0), 0);
    }
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial focus
  useEffect(() => {
    if (autoFocus) focusAt(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const raw = e.target.value.replace(/\D/g, '');
      const digit = raw.slice(-1); // take last digit; handles mobile multi-char input
      const newDigits = [...digits];
      newDigits[index] = digit;
      setDigits(newDigits);
      if (digit && index < LENGTH - 1) focusAt(index + 1);
      if (digit && newDigits.every(Boolean)) onComplete(newDigits.join(''));
    },
    [digits, focusAt, onComplete]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace') {
        if (digits[index]) {
          // Clear current box via change handler — let it fire naturally
          return;
        }
        // Empty box: move back and clear previous
        if (index > 0) {
          e.preventDefault();
          const newDigits = [...digits];
          newDigits[index - 1] = '';
          setDigits(newDigits);
          focusAt(index - 1);
        }
        return;
      }
      if (e.key === 'ArrowLeft') { e.preventDefault(); focusAt(index - 1); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); focusAt(index + 1); return; }
    },
    [digits, focusAt]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
      if (!text) return;
      const newDigits = Array(LENGTH).fill('') as string[];
      for (let i = 0; i < text.length; i++) newDigits[i] = text[i];
      setDigits(newDigits);
      focusAt(Math.min(text.length, LENGTH - 1));
      if (text.length === LENGTH) onComplete(text);
    },
    [focusAt, onComplete]
  );

  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', padding: '4px 0' }}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={2}
          value={digit}
          disabled={disabled}
          style={{
            width: 46,
            height: 54,
            textAlign: 'center',
            fontSize: 22,
            fontWeight: 700,
            borderRadius: 8,
            border: `1.5px solid ${
              error ? DANGER.tag : digit ? BLUE.badge : '#d9d9d9'
            }`,
            outline: 'none',
            caretColor: 'transparent',
            color: INK,
            background: error ? DANGER.soft : digit ? CHROME.bg : '#fff',
            transition: 'border-color 0.15s, background 0.15s',
            cursor: disabled ? 'not-allowed' : 'text',
            boxShadow: digit && !error ? '0 0 0 2px rgba(22,119,255,0.1)' : undefined,
          }}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
};
